import type { InfiniteData } from "@tanstack/react-query";
import type { ListMessagesResponse, MessageSummary, SystemFolder } from "@mailflare/shared";

export type MessagePages = InfiniteData<ListMessagesResponse, string | null>;

export interface MessageSummaryPatch {
  readonly isRead?: boolean;
  readonly isFlagged?: boolean;
  readonly folder?: SystemFolder;
}

export function applySummaryPatch(
  summary: MessageSummary,
  patch: MessageSummaryPatch,
): MessageSummary {
  return {
    ...summary,
    ...(patch.isRead !== undefined ? { isRead: patch.isRead } : {}),
    ...(patch.isFlagged !== undefined ? { isFlagged: patch.isFlagged } : {}),
    ...(patch.folder !== undefined ? { folder: patch.folder } : {}),
  };
}

export function patchMessagesInPages(
  data: MessagePages | undefined,
  ids: readonly string[],
  patch: MessageSummaryPatch,
): MessagePages | undefined {
  if (data === undefined) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.map((message) =>
        ids.includes(message.id) ? applySummaryPatch(message, patch) : message,
      ),
    })),
  };
}

export function removeMessagesFromPages(
  data: MessagePages | undefined,
  ids: readonly string[],
): MessagePages | undefined {
  if (data === undefined) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.filter((message) => !ids.includes(message.id)),
    })),
  };
}

/** Adds or removes a label id from a cached summary. */
export function toggleLabelInPages(
  data: MessagePages | undefined,
  ids: readonly string[],
  labelId: string,
  present: boolean,
): MessagePages | undefined {
  if (data === undefined) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.map((message) => {
        if (!ids.includes(message.id)) return message;
        const labelIds = present
          ? Array.from(
              new Set([...message.labelIds, labelId as MessageSummary["labelIds"][number]]),
            )
          : message.labelIds.filter((id) => id !== labelId);
        return { ...message, labelIds };
      }),
    })),
  };
}
